import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import axios from "axios";
import { FiArrowLeft, FiLoader, FiAlertCircle, FiBookOpen } from "react-icons/fi";
import HeartLesson from "./HeartLesson";

// ---------------------------------------------------------
// Props & Types
// ---------------------------------------------------------
interface LessonViewerProps {
  grade: string;
  term: number;
  subject: string;
  strand: string;
  subStrand: string;
  onBack?: () => void;
}

interface LessonModule {
  type: string;
  title: string;
  content: string;
  order: number;
  media?: {
    type: string;
    url: string;
    alt?: string;
    description?: string;
    duration?: string;
  };
  sections?: {
    title: string;
    content: string;
    image?: string;
  }[];
  instructions?: string[];
  worksheet?: {
    title: string;
    questions: string[];
  };
  settings?: {
    questions: {
      question: string;
      options: string[];
      correctAnswer: string;
      explanation: string;
    }[];
  };
}

interface Lesson {
  id: string;
  title: string;
  content: string | {
    type: string;
    component: string;
  };
  modules?: LessonModule[];
}

// ---------------------------------------------------------
// API Call Function
// ---------------------------------------------------------
const getLessonContent = async (
  grade: string,
  term: number,
  subject: string,
  strand: string,
  subStrand: string
): Promise<Lesson> => {
  try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://lavernaibackend.onrender.com/api'}/lessons`, {
      params: {
        grade,
        term,
        subject,
        strand,
        subStrand: subStrand,
      },
    });

    if (response.data.success && response.data.data && response.data.data.lesson) {
      return response.data.data.lesson;
    } else {
      throw new Error("Lesson not found for the provided filters");
    }
  } catch (error: any) {
    console.error("Error fetching lesson content:", error);
    throw new Error(
      error.response?.data?.message || "Failed to load lesson content."
    );
  }
};

// ---------------------------------------------------------
// Styled Components
// ---------------------------------------------------------
const Container = styled.div`
  max-width: 800px;
  margin: 30px auto;
  padding: 30px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(126, 34, 206, 0.05);
    color: ${({ theme }) => theme.primaryDark || "#5a17a7"};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(126, 34, 206, 0.2);
  }
`;

const LessonMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const MetaTag = styled.span`
  background-color: ${({ theme }) => theme.primaryLight || "#f3e8ff"};
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  margin-bottom: 20px;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
  flex-grow: 1;
`;

const Content = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
  white-space: pre-wrap;
  
  h1, h2, h3, h4, h5, h6 {
    color: ${({ theme }) => theme.secondary || "#333"};
    margin-top: 24px;
    margin-bottom: 16px;
  }
  
  p {
    margin-bottom: 16px;
  }
  
  ul, ol {
    margin-left: 24px;
    margin-bottom: 16px;
  }
  
  img {
    max-width: 100%;
    border-radius: 8px;
    margin: 16px 0;
  }
`;

const ModuleContainer = styled.div`
  margin-top: 32px;
  padding: 24px;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const ModuleTitle = styled.h3`
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  margin-bottom: 16px;
  font-size: 1.5rem;
`;

const MediaContainer = styled.div`
  margin: 16px 0;
  
  img {
    max-width: 100%;
    border-radius: 8px;
  }
  
  video {
    max-width: 100%;
    border-radius: 8px;
  }
`;

const SectionContainer = styled.div`
  margin: 16px 0;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h4`
  color: ${({ theme }) => theme.secondary || "#333"};
  margin-bottom: 12px;
  font-size: 1.2rem;
`;

const InstructionsList = styled.ol`
  margin-left: 24px;
  margin-bottom: 16px;
  
  li {
    margin-bottom: 8px;
  }
`;

const WorksheetContainer = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const QuestionContainer = styled.div`
  margin: 16px 0;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  gap: 16px;
`;

const SpinningIcon = styled(FiLoader)`
  animation: spin 1s linear infinite;
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  font-size: 2rem;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
  text-align: center;
`;

const ErrorIcon = styled(FiAlertCircle)`
  color: #e53e3e;
  font-size: 2.5rem;
`;

const ErrorMessage = styled.p`
  color: #e53e3e;
  font-size: 1.1rem;
  margin-bottom: 16px;
`;

const RetryButton = styled.button`
  background-color: ${({ theme }) => theme.primary || "#7e22ce"};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.primaryDark || "#5a17a7"};
  }
`;

// ---------------------------------------------------------
// LessonViewer Component
// ---------------------------------------------------------
const LessonViewer: React.FC<LessonViewerProps> = ({
  grade,
  term,
  subject,
  strand,
  subStrand,
  onBack,
}) => {
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const fetchLesson = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const lessonData = await getLessonContent(
        grade,
        term,
        subject,
        strand,
        subStrand
      );
      setLesson(lessonData);
    } catch (err: any) {
      setError(err.message || "Failed to load lesson content.");
    } finally {
      setLoading(false);
    }
  }, [grade, term, subject, strand, subStrand]);

  useEffect(() => {
    fetchLesson();
  }, [fetchLesson]);

  const renderMetaTags = () => (
    <LessonMeta>
      <MetaTag>Grade: {grade}</MetaTag>
      <MetaTag>Term: {term}</MetaTag>
      <MetaTag>Subject: {subject}</MetaTag>
      <MetaTag>Strand: {strand}</MetaTag>
      <MetaTag>Sub-strand: {subStrand}</MetaTag>
    </LessonMeta>
  );

  const renderModule = (module: LessonModule) => {
    // Create a unique key using module order or a fallback
    const moduleKey = `module-${module.order ?? module.title ?? 'default'}`;
    
    return (
      <React.Fragment key={moduleKey}>
        <ModuleContainer>
          <ModuleTitle>{module.title}</ModuleTitle>
          <Content>{module.content}</Content>
          
          {module.media && (
            <MediaContainer key={`media-${moduleKey}`}>
              {module.media.type === "image" && (
                <img key={`image-${moduleKey}`} src={module.media.url} alt={module.media.alt || ""} />
              )}
              {module.media.type === "video" && (
                <video key={`video-${moduleKey}`} controls>
                  <source src={module.media.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
              {module.media.type === "interactive" && (
                <div key={`interactive-${moduleKey}`}>
                  <p>{module.media.description}</p>
                  <iframe
                    src={module.media.url}
                    title={module.title}
                    width="100%"
                    height="400px"
                    frameBorder="0"
                  />
                </div>
              )}
            </MediaContainer>
          )}

          {module.sections && module.sections.map((section, index) => (
            <SectionContainer key={`section-${moduleKey}-${index}`}>
              <SectionTitle>{section.title}</SectionTitle>
              <Content>{section.content}</Content>
              {section.image && (
                <img key={`section-image-${moduleKey}-${index}`} src={section.image} alt={section.title} />
              )}
            </SectionContainer>
          ))}

          {module.instructions && (
            <InstructionsList key={`instructions-${moduleKey}`}>
              {module.instructions.map((instruction, index) => (
                <li key={`instruction-${moduleKey}-${index}`}>{instruction}</li>
              ))}
            </InstructionsList>
          )}

          {module.worksheet && (
            <WorksheetContainer key={`worksheet-${moduleKey}`}>
              <SectionTitle>{module.worksheet.title}</SectionTitle>
              {module.worksheet.questions.map((question, index) => (
                <QuestionContainer key={`worksheet-question-${moduleKey}-${index}`}>
                  <p>{question}</p>
                  <textarea
                    placeholder="Your answer here..."
                    style={{
                      width: "100%",
                      minHeight: "100px",
                      marginTop: "8px",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ddd"
                    }}
                  />
                </QuestionContainer>
              ))}
            </WorksheetContainer>
          )}

          {module.settings?.questions && (
            <div key={`settings-${moduleKey}`}>
              {module.settings.questions.map((question, index) => (
                <QuestionContainer key={`settings-question-${moduleKey}-${index}`}>
                  <p><strong>{question.question}</strong></p>
                  {question.options.map((option, optIndex) => (
                    <div key={`option-${moduleKey}-${index}-${optIndex}`} style={{ margin: "8px 0" }}>
                      <input
                        type="radio"
                        name={`question-${index}`}
                        id={`option-${index}-${optIndex}`}
                      />
                      <label htmlFor={`option-${index}-${optIndex}`}>
                        {option}
                      </label>
                    </div>
                  ))}
                  {question.explanation && (
                    <p style={{ marginTop: "8px", color: "#666" }}>
                      <em>{question.explanation}</em>
                    </p>
                  )}
                </QuestionContainer>
              ))}
            </div>
          )}
        </ModuleContainer>
      </React.Fragment>
    );
  };

  const renderContent = () => {
    if (!lesson) return null;

    if (typeof lesson.content === 'string') {
      return <Content>{lesson.content}</Content>;
    }

    // Handle interactive content
    if (lesson.content.type === 'interactive') {
      switch (lesson.content.component) {
        case 'HeartLesson':
          return <HeartLesson />;
        default:
          return <Content>Interactive content not available</Content>;
      }
    }

    return <Content>Content not available</Content>;
  };

  return (
    <Container>
      <Header>
        {onBack && (
          <BackButton onClick={onBack}>
            <FiArrowLeft style={{ marginRight: "8px" }} /> Back
          </BackButton>
        )}
        {!loading && !error && lesson && (
          <Title>{lesson.title}</Title>
        )}
      </Header>

      {loading ? (
        <LoaderContainer>
          <SpinningIcon />
          <p>Loading lesson content...</p>
        </LoaderContainer>
      ) : error ? (
        <ErrorContainer>
          <ErrorIcon />
          <ErrorMessage>{error}</ErrorMessage>
          <RetryButton onClick={fetchLesson}>Try Again</RetryButton>
        </ErrorContainer>
      ) : lesson ? (
        <>
          {renderMetaTags()}
          {renderContent()}
          {lesson.modules && lesson.modules
            .sort((a, b) => a.order - b.order)
            .map(renderModule)}
        </>
      ) : (
        <ErrorContainer>
          <FiBookOpen style={{ fontSize: "2.5rem", color: "#a0aec0" }} />
          <p>No lesson content available for the selected filters.</p>
        </ErrorContainer>
      )}
    </Container>
  );
};

export default LessonViewer;